import * as React from 'react';
import { useMemo } from 'react';
import { Alert } from 'reactstrap';
import { useTranslations, getLanguage } from '../../../store/translations/hooks';
import { TK } from '../../../store/translations/translationKeys';
import { CommunicationQuotas, RemindersSettingsData } from '../../../models';
import Typography from '../../../components/Typography';
import { useDispatch } from 'react-redux';
import { alertError, alertGenericError, alertSuccess } from '../../../store/alerts/actions';
import { testReminderEmailAsync, testReminderSmsAsync, testReminderWhatsAppAsync } from '../../../ajax/requests';
import { getErrorMessage, isConflictError } from '../../../ajax/errors';
import { SegmentedMessage } from 'sms-segments-calculator';
import { CheckInputField } from '../../../components/Forms/fields/CheckInputField';
import { AutocompleteTextField, TextInputField, MultipleCheckboxes, PlaceholderTextField } from '../../../components/Forms/fields';
import { useGeneralSettings } from '../../../store/settings/hooks';
import { serviceId, serviceName } from '../../../shared/appointmint/appointment';
import { Dialog, DialogContent, DialogTitle, IconButton } from '@mui/material';
import HelpIcon from '@material-ui/icons/Help';
import { paths } from '../../../app/constants';
// Lazy load HtmlEditor
const HtmlEditor = React.lazy(() => import('../../Campaigns/HtmlEditor').then(m => ({ default: m.HtmlEditor })));
import {
    ChannelCard, ChannelHeader, ChannelInfo, ChannelContent, ChannelStatus, ChannelQuota,
    ChannelWarning, MySubscriptionLink, TestSection, TestActionButton, getMessageIcon, buildTestData,
    normalizeEmailReminderSettings,
    normalizeReminderServiceTypesKeys, shouldShowNoServiceTypesWarning,
} from './remindersShared';

export interface ClientRemindersTabProps {
    values: RemindersSettingsData;
    onChange: (reminders: RemindersSettingsData) => void;
    communicationQuotas: CommunicationQuotas | null;
}

const ClientRemindersTab: React.FC<ClientRemindersTabProps> = ({ values, onChange, communicationQuotas }) => {
    const t = useTranslations();
    const dispatch = useDispatch();
    const generalSettings = useGeneralSettings();
    const culture = getLanguage();
    const privacyPolicyUrl = `https://appointmint.pt/${culture}/privacy`;
    const [whatsAppHelpContext, setWhatsAppHelpContext] = React.useState<'client' | null>(null);

    const [expandedChannel, setExpandedChannel] = React.useState<'sms' | 'whatsapp' | 'email' | null>(() => {
        if (values.smsHoursBeforeAppointment?.length || values.smsOnAppointmentCreation) {
            return 'sms';
        }
        if (values.whatsAppHoursBeforeAppointment?.length || values.whatsAppOnAppointmentCreation) {
            return 'whatsapp';
        }
        if (values.emailHoursBeforeAppointment?.length || values.emailOnAppointmentCreation) {
            return 'email';
        }
        return null;
    });

    const [emailTest, setEmailTest] = React.useState('');
    const [phoneNumberTest, setPhoneTest] = React.useState('');
    const [testing, setTesting] = React.useState(false);

    const companyName = generalSettings.companyName ?? '';

    const getTranslationWithFallback = React.useCallback((key: TK, fallback: string) => {
        const translated = t(key);
        return translated === key ? fallback : translated;
    }, [t]);

    const whatsAppDisclaimerStart = getTranslationWithFallback(
        TK.whatsAppMetaReviewDisclaimerStart,
        'Appointmint uses its own phone number'
    );
    const whatsAppDisclaimerEnd = getTranslationWithFallback(
        TK.whatsAppMetaReviewDisclaimerEnd,
        'to communicate with your clients by WhatsApp, send reminders and confirmations, receive responses, and all communications are visible in the client profile communication chat.'
    );
    const whatsAppDefaultTemplate = getTranslationWithFallback(
        TK.whatsAppReminderDefaultTemplate,
        `Hi {{client_name}}!
{{company_name}} reminds you of your {{appointment_type}} appointment with {{professional_name}} on {{day}} ({{week_day}}), at {{time}}.
If you cannot attend, please contact the clinic.
Thank you and see you soon!`
    );

    const smsDefaultText = useMemo(() =>
        getTranslationWithFallback(
            TK.smsDefaultText,
            `Hi {client-name}!\n{companyName} reminds you of your {appointment-type} appointment with {professional-name} on {day} ({week-day}), at {time}.\nIf you cannot attend, please contact us.\nThank you and see you soon!`
        ).replace(/{companyName}/g, companyName),
    [getTranslationWithFallback, companyName]);

    const emailDefaultSubject = useMemo(() =>
        getTranslationWithFallback(
            TK.emailDefaultSubject,
            '{companyName} - Appointment Reminder'
        ).replace(/{companyName}/g, companyName),
    [getTranslationWithFallback, companyName]);

    const emailDefaultText = useMemo(() =>
        getTranslationWithFallback(
            TK.emailDefaultText,
            `<p>Hi {client-name}!</p><p> </p><p>{companyName} reminds you of your <b>{appointment-type}</b> appointment with <b>{professional-name}</b> on <b>{day}</b> (<b>{week-day}</b>), at <b>{time}</b>.</p><p>If you cannot attend, please contact us.</p><p> </p><p>Thank you and see you soon!</p>`
        ).replace(/{companyName}/g, companyName),
    [getTranslationWithFallback, companyName]);

    const remindersFallbackDisclaimerText = t(TK.remindersWhatsAppSmsFallbackDisclaimer);

    const smsNrSegments = useMemo(() => new SegmentedMessage(values.smsText ?? '').segmentsCount, [values.smsText]);

    const hoursMap = useMemo(() => ({
        [t(TK.twoHours)]: 2,
        [t(TK.fourHours)]: 4,
        [t(TK.oneDay)]: 24,
        [t(TK.twoDays)]: 24*2,
        [t(TK.threeDays)]: 24*3,
        [t(TK.oneWeek)]: 24*7,
    }), []);

    const serviceTypeOptions = useMemo(() => Object.keys(generalSettings.services ?? {}), [generalSettings.services]);
    const sendToAllServicesTypes = values.servicesTypesKeys == null;
    const selectedServiceTypesKeys = useMemo(
        () => normalizeReminderServiceTypesKeys(values, serviceTypeOptions),
        [values, serviceTypeOptions],
    );
    const showNoServiceTypesWarning = shouldShowNoServiceTypesWarning(sendToAllServicesTypes, selectedServiceTypesKeys);

    const buildUsageLabel = React.useCallback((used?: number, allowed?: number) =>
        used !== undefined && allowed !== undefined ? `${Math.min(used, allowed)}/${allowed}` : null
    , []);

    const smsUsageLabel = buildUsageLabel(communicationQuotas?.smsUsed, communicationQuotas?.smsAllowed);
    const whatsAppUsed = communicationQuotas?.whatsAppUsed;
    const whatsAppAllowed = communicationQuotas?.whatsAppAllowed;
    const whatsAppUsageLabel = buildUsageLabel(whatsAppUsed, whatsAppAllowed);
    const whatsAppQuotaExhausted = whatsAppUsed !== undefined
        && whatsAppAllowed !== undefined
        && whatsAppUsed >= whatsAppAllowed;

    const getReminderChannelUsageLabel = React.useCallback((channel: 'sms' | 'whatsapp' | 'email') => {
        if (channel === 'sms') return smsUsageLabel;
        if (channel === 'email') return null;
        return whatsAppUsageLabel;
    }, [smsUsageLabel, whatsAppUsageLabel]);

    const isChannelActive = React.useCallback((channel: 'sms' | 'whatsapp' | 'email') => {
        if (channel === 'sms') {
            return !!(values.smsHoursBeforeAppointment?.length || values.smsOnAppointmentCreation);
        }
        if (channel === 'whatsapp') {
            return !!(values.whatsAppHoursBeforeAppointment?.length || values.whatsAppOnAppointmentCreation);
        }
        return !!(values.emailHoursBeforeAppointment?.length || values.emailOnAppointmentCreation);
    }, [values]);

    const channelStates = useMemo(() => ({
        sms: isChannelActive('sms'),
        whatsapp: isChannelActive('whatsapp'),
        email: isChannelActive('email'),
    }), [isChannelActive]);

    const catchRequestError = React.useCallback((error: unknown) => {
        const message = getErrorMessage(error);
        if (message) {
            dispatch(alertError(message));
            return;
        }

        dispatch(isConflictError(error)
            ? alertError(t(TK.plafontExausted))
            : alertGenericError())
    }, [dispatch, t]);

    const handleTestEmail = React.useCallback(() => {
        if(!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/.test(emailTest)) {
            dispatch(alertError(t(TK.invalidEmail)));
            return;
        }

        const { emailName, emailSubject, emailText } = normalizeEmailReminderSettings(
            values,
            companyName,
            emailDefaultSubject,
            emailDefaultText,
        );

        setTesting(true);
        testReminderEmailAsync(buildTestData({
            ...values,
            emailName,
            emailSubject,
            emailText,
        }, emailTest, false))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchRequestError)
            .finally(() => setTesting(false));
    }, [emailTest, values, dispatch, catchRequestError, t, companyName, emailDefaultSubject, emailDefaultText]);

    const handleTestSms = React.useCallback((isConfirmationRequest: boolean) => {
        if(!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) {
            dispatch(alertError(t(TK.invalidPhoneNumber)));
            return;
        }
        setTesting(true);
        testReminderSmsAsync(buildTestData(values, phoneNumberTest, isConfirmationRequest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchRequestError)
            .finally(() => setTesting(false));
    }, [phoneNumberTest, values, dispatch, catchRequestError, t]);

    const handleTestWhatsApp = React.useCallback((isConfirmationRequest: boolean) => {
        if(!/^((\+\d{12})|(\d{9}))$/.test(phoneNumberTest)) {
            dispatch(alertError(t(TK.invalidPhoneNumber)));
            return;
        }
        setTesting(true);
        testReminderWhatsAppAsync(buildTestData(values, phoneNumberTest, isConfirmationRequest))
            .then(() => dispatch(alertSuccess(t(TK.sent))))
            .catch(catchRequestError)
            .finally(() => setTesting(false));
    }, [phoneNumberTest, values, dispatch, catchRequestError, t]);

    const onChangeReminders = (reminders: RemindersSettingsData) => onChange(reminders);

    const enableSmsDefaults = () => {
        if (values.smsHoursBeforeAppointment?.length || values.smsOnAppointmentCreation) {
            return;
        }
        onChangeReminders({
            ...values,
            smsText: values.smsText || smsDefaultText,
            smsHoursBeforeAppointment: [24],
        });
    };

    const enableWhatsAppDefaults = () => {
        if (values.whatsAppHoursBeforeAppointment?.length || values.whatsAppOnAppointmentCreation) {
            return;
        }
        onChangeReminders({
            ...values,
            whatsAppText: values.whatsAppText ?? whatsAppDefaultTemplate,
            whatsAppHoursBeforeAppointment: [24],
        });
    };

    const enableEmailDefaults = () => {
        if (values.emailHoursBeforeAppointment?.length || values.emailOnAppointmentCreation) {
            return;
        }
        onChangeReminders({
            ...values,
            emailName: values.emailName || companyName,
            emailSubject: values.emailSubject || emailDefaultSubject,
            emailText: values.emailText || emailDefaultText,
            emailHoursBeforeAppointment: [24],
        });
    };

    const getChannelLabel = (channel: 'sms' | 'whatsapp' | 'email') =>
        channel === 'sms' ? t(TK.sms) : channel === 'whatsapp' ? 'WhatsApp' : t(TK.email);

    const getChannelIcon = (channel: 'sms' | 'whatsapp' | 'email') =>
        channel === 'sms' ? getMessageIcon('sms') : channel === 'whatsapp' ? getMessageIcon('whatsapp') : getMessageIcon('email');

    React.useEffect(() => {
        const smsActive = isChannelActive('sms');
        const smsText = values.smsText || smsDefaultText;
        if (smsActive && values.smsText !== smsText) {
            onChange({
                ...values,
                smsText,
            });
        }
    }, [isChannelActive, values, onChange, smsDefaultText]);

    React.useEffect(() => {
        const whatsappActive = isChannelActive('whatsapp');
        if (whatsappActive && !values.whatsAppText) {
            onChange({
                ...values,
                whatsAppText: whatsAppDefaultTemplate,
            });
        }
    }, [isChannelActive, values, onChange, whatsAppDefaultTemplate]);

    React.useEffect(() => {
        const emailActive = isChannelActive('email');
        const { emailName, emailSubject, emailText } = normalizeEmailReminderSettings(
            values,
            companyName,
            emailDefaultSubject,
            emailDefaultText,
        );

        if (emailActive && (
            values.emailName !== emailName
            || values.emailSubject !== emailSubject
            || values.emailText !== emailText
        )) {
            onChange({
                ...values,
                emailName,
                emailSubject,
                emailText,
            });
        }
    }, [isChannelActive, values, onChange, companyName, emailDefaultSubject, emailDefaultText]);

    const renderChannelConfig = (channel: 'sms' | 'whatsapp' | 'email') => {
        if (channel === 'sms') {
            return (
                <>
                    <Typography size="smaller" style={{color: 'var(--text-muted)'}}>
                        {t(TK.reminderSmsWarningMessage)}
                    </Typography>
                    <PlaceholderTextField
                        id={`clients-reminders-sms-text`}
                        label={t(TK.message)}
                        value={values.smsText || smsDefaultText}
                        type='textarea'
                        maxLength={500}
                        onChange={smsText => onChangeReminders({...values, smsText})}
                    />
                    {smsNrSegments > 1 && (
                        <Typography size='small' style={{color: 'var(--danger-color)'}}>
                            {t(TK.smsSegmentsWarning)} <b>{t(TK.smsNumberOfSegments)}: {smsNrSegments}</b>
                        </Typography>
                    )}

                    <Typography size="large"><b>{t(TK.whenToSend)}</b></Typography>
                    <Typography size="smaller" style={{color: 'var(--text-muted)'}}>{t(TK.whenToSendNote)}</Typography>
                    <CheckInputField
                        label={t(TK.onCreationAndEdition)}
                        value={values.smsOnAppointmentCreation ?? false}
                        onChange={smsOnAppointmentCreation => onChangeReminders({...values, smsOnAppointmentCreation})}
                    />
                    <MultipleCheckboxes
                        values={values.smsHoursBeforeAppointment?.map(hour =>
                            Object.keys(hoursMap).find(key => hoursMap[key] === hour) ?? ''
                        ) ?? []}
                        options={Object.keys(hoursMap)}
                        onChange={options => {
                            const smsHoursBeforeAppointment = options.map(option => hoursMap[option]);
                            onChangeReminders({...values, smsHoursBeforeAppointment});
                        }}
                    />

                    <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                    <TestSection>
                        <TextInputField
                            style={{flexGrow: 1, minWidth: '200px'}}
                            value={phoneNumberTest}
                            onChange={setPhoneTest}
                            placeholder="+351910000000"
                        />
                        <TestActionButton disabled={testing} onClick={() => handleTestSms(false)}>
                            {getMessageIcon('sms', {style: {marginRight: 'var(--spacer-XXS)'}})}
                            {t(TK.testSms)}
                        </TestActionButton>
                    </TestSection>
                </>
            );
        }

        if (channel === 'whatsapp') {
            const whatsAppTemplate = values.whatsAppText ?? whatsAppDefaultTemplate;
            return (
                <>
                    <Typography size="smaller" style={{color: 'var(--text-muted)'}}>
                        {t(TK.whatsAppTemplateMetaWarning)}
                    </Typography>
                    <PlaceholderTextField
                        id={`clients-reminders-whatsapp-text`}
                        label={t(TK.message)}
                        value={whatsAppTemplate}
                        type='textarea'
                        disabled
                        maxLength={500}
                    />

                    <Typography size="large"><b>{t(TK.whenToSend)}</b></Typography>
                    <Typography size="smaller" style={{color: 'var(--text-muted)'}}>{t(TK.whenToSendNote)}</Typography>
                    <CheckInputField
                        label={t(TK.onCreationAndEdition)}
                        value={values.whatsAppOnAppointmentCreation ?? false}
                        onChange={whatsAppOnAppointmentCreation => onChangeReminders({...values, whatsAppOnAppointmentCreation})}
                    />
                    <MultipleCheckboxes
                        values={values.whatsAppHoursBeforeAppointment?.map(hour =>
                            Object.keys(hoursMap).find(key => hoursMap[key] === hour) ?? ''
                        ) ?? []}
                        options={Object.keys(hoursMap)}
                        onChange={options => {
                            const whatsAppHoursBeforeAppointment = options.map(option => hoursMap[option]);
                            onChangeReminders({...values, whatsAppHoursBeforeAppointment});
                        }}
                    />

                    <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                    <TestSection>
                        <TextInputField
                            style={{flexGrow: 1, minWidth: '200px'}}
                            value={phoneNumberTest}
                            onChange={setPhoneTest}
                            placeholder="+351910000000"
                        />
                        <TestActionButton disabled={testing} onClick={() => handleTestWhatsApp(false)}>
                            {getMessageIcon('whatsapp', {style: {marginRight: 'var(--spacer-XXS)'}})}
                            {t(TK.testWhatsApp)}
                        </TestActionButton>
                    </TestSection>
                </>
            );
        }

        return (
            <>
                <Typography size="large"><b>{t(TK.emailSettings)}</b></Typography>
                <TextInputField
                    label={t(TK.fromName)}
                    value={values.emailName || companyName}
                    onChange={emailName => onChangeReminders({...values, emailName})}
                />
                <TextInputField
                    label={t(TK.fromEmail)}
                    helpText={t(TK.fromEmailWarningMessage)}
                    placeholder={t(TK.fromEmailWarningMessage)}
                    value={values.emailFrom ?? ''}
                    onChange={emailFrom => /^[a-z0-9._+-@]*$/.test(emailFrom) && onChangeReminders({...values, emailFrom})}
                />
                <TextInputField
                    label={t(TK.subject)}
                    value={values.emailSubject || emailDefaultSubject}
                    onChange={emailSubject => onChangeReminders({...values, emailSubject})}
                />

                <Typography size="large"><b>{t(TK.message)}</b></Typography>
                <React.Suspense fallback={<div style={{ padding: 'var(--spacer-M)', textAlign: 'center' }}>Loading editor...</div>}>
                    <HtmlEditor
                        value={values.emailText || emailDefaultText}
                        onChange={emailText => onChangeReminders({...values, emailText})}
                    />
                </React.Suspense>

                <Typography size="large"><b>{t(TK.whenToSend)}</b></Typography>
                <Typography size="smaller" style={{color: 'var(--text-muted)'}}>{t(TK.whenToSendNote)}</Typography>
                <CheckInputField
                    label={t(TK.onCreationAndEdition)}
                    value={values.emailOnAppointmentCreation ?? false}
                    onChange={emailOnAppointmentCreation => onChangeReminders({...values, emailOnAppointmentCreation})}
                />
                <MultipleCheckboxes
                    values={values.emailHoursBeforeAppointment?.map(hour =>
                        Object.keys(hoursMap).find(key => hoursMap[key] === hour) ?? ''
                    ) ?? []}
                    options={Object.keys(hoursMap)}
                    onChange={options => {
                        const emailHoursBeforeAppointment = options.map(option => hoursMap[option]);
                        onChangeReminders({...values, emailHoursBeforeAppointment});
                    }}
                />

                <Typography size="large"><b>{t(TK.testReminder)}</b></Typography>
                <TestSection>
                    <TextInputField
                        style={{flexGrow: 1, minWidth: '200px'}}
                        value={emailTest}
                        onChange={setEmailTest}
                        placeholder="xpto@domain.com"
                    />
                    <TestActionButton disabled={testing} onClick={handleTestEmail}>
                        {getMessageIcon('email', {style: {marginRight: 'var(--spacer-XXS)'}})}
                        {t(TK.testEmail)}
                    </TestActionButton>
                </TestSection>
            </>
        );
    };

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
            <Typography size="large" style={{marginBottom: 'var(--spacer-S)'}}><b>{t(TK.communicationChannels)}</b></Typography>
            <Alert color="secondary" isOpen>
                {remindersFallbackDisclaimerText}
            </Alert>

            <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)'}}>
                {(['sms', 'whatsapp', 'email'] as const).map(channel => {
                    const isWhatsAppChannel = channel === 'whatsapp';
                    const isWhatsAppCheckboxDisabled = isWhatsAppChannel && whatsAppQuotaExhausted && !channelStates[channel];
                    const channelUsageLabel = getReminderChannelUsageLabel(channel);
                    return (
                        <ChannelCard key={channel} $isActive={channelStates[channel]} $isSelected={expandedChannel === channel}>
                            <ChannelHeader
                                $isActive={channelStates[channel]}
                                $isSelected={expandedChannel === channel}
                                onClick={() => {
                                    setExpandedChannel(expandedChannel === channel ? null : channel)
                                }}
                            >
                                <CheckInputField
                                    label=""
                                    value={channelStates[channel]}
                                    disabled={isWhatsAppCheckboxDisabled}
                                    onChange={enabled => {
                                        if (!enabled) {
                                            if (channel === 'sms') {
                                                onChangeReminders({
                                                    ...values,
                                                    smsHoursBeforeAppointment: undefined,
                                                    smsOnAppointmentCreation: false
                                                });
                                                if (expandedChannel === 'sms') setExpandedChannel(null);
                                            }
                                            if (channel === 'whatsapp') {
                                                onChangeReminders({
                                                    ...values,
                                                    whatsAppHoursBeforeAppointment: undefined,
                                                    whatsAppOnAppointmentCreation: false
                                                });
                                                if (expandedChannel === 'whatsapp') setExpandedChannel(null);
                                            }
                                            if (channel === 'email') {
                                                onChangeReminders({
                                                    ...values,
                                                    emailHoursBeforeAppointment: undefined,
                                                    emailOnAppointmentCreation: false
                                                });
                                                if (expandedChannel === 'email') setExpandedChannel(null);
                                            }
                                        } else {
                                            if (channel === 'sms') enableSmsDefaults();
                                            if (channel === 'whatsapp') {
                                                if (isWhatsAppCheckboxDisabled) return;
                                                enableWhatsAppDefaults();
                                            }
                                            if (channel === 'email') enableEmailDefaults();
                                            setExpandedChannel(channel);
                                        }
                                    }}
                                    onClick={e => e.stopPropagation()}
                                />
                                <ChannelInfo>
                                    {getChannelIcon(channel)}
                                    <Typography>
                                        <b>
                                            <span style={{display: 'inline-flex', alignItems: 'center', gap: 'var(--spacer-XXS)', flexWrap: 'wrap'}}>
                                                <span>{channel === 'whatsapp' ? 'WhatsApp' : getChannelLabel(channel)}</span>
                                                {channelUsageLabel && <ChannelQuota>({channelUsageLabel})</ChannelQuota>}
                                                {channel === 'whatsapp' && (
                                                    <IconButton
                                                        size="small"
                                                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                                            e.stopPropagation();
                                                            setWhatsAppHelpContext('client');
                                                        }}
                                                        style={{padding: 2}}
                                                    >
                                                        <HelpIcon style={{fontSize: '1rem'}} />
                                                    </IconButton>
                                                )}
                                            </span>
                                        </b>
                                    </Typography>
                                    <ChannelStatus $isActive={channelStates[channel]}>
                                        {channelStates[channel] ? t(TK.active) : t(TK.inactive)}
                                    </ChannelStatus>
                                    {isWhatsAppChannel && isWhatsAppCheckboxDisabled && (
                                        <ChannelWarning onClick={(event: React.MouseEvent<HTMLSpanElement>) => event.stopPropagation()}>
                                            {t(TK.noWhatsAppQuotaAvailableAddMoreIn)}{' '}
                                            <MySubscriptionLink to={paths.mySubscription()} onClick={event => event.stopPropagation()}>
                                                {t(TK.mySubscription)}
                                            </MySubscriptionLink>.
                                        </ChannelWarning>
                                    )}
                                </ChannelInfo>
                            </ChannelHeader>
                            {expandedChannel === channel && (
                                <ChannelContent>
                                    {renderChannelConfig(channel)}
                                </ChannelContent>
                            )}
                        </ChannelCard>
                    );
                })}
            </div>

            <div style={{display: 'flex', flexDirection: 'column', gap: 'var(--spacer-S)'}}>
                <CheckInputField
                    label={t(TK.sendToAllServicesTypes)}
                    value={sendToAllServicesTypes}
                    onChange={enabled => {
                        if (enabled) {
                            onChangeReminders({
                                ...values,
                                servicesTypesKeys: undefined,
                            });
                            return;
                        }

                        onChangeReminders({
                            ...values,
                            servicesTypesKeys: normalizeReminderServiceTypesKeys(values, serviceTypeOptions),
                        });
                    }}
                />

                {!sendToAllServicesTypes && (
                    <AutocompleteTextField
                        label={t(TK.services)}
                        helpText={t(TK.remindersServiceTypesHelpNote)}
                        multiSelect
                        options={serviceTypeOptions.map(k => serviceName(k, generalSettings))}
                        values={selectedServiceTypesKeys.map(k => serviceName(k, generalSettings))}
                        onValuesChanged={displayNames => onChangeReminders({
                            ...values,
                            servicesTypesKeys: displayNames.map(n => serviceId(n, generalSettings)),
                        })}
                    />
                )}

                {showNoServiceTypesWarning && (
                    <Alert color="warning" isOpen>
                        {t(TK.remindersNoServiceTypesSelectedWarning)}
                    </Alert>
                )}
            </div>

            <Dialog open={whatsAppHelpContext !== null} onClose={() => setWhatsAppHelpContext(null)}>
                <DialogTitle>{t(TK.clientReminders)}</DialogTitle>
                <DialogContent>
                    <div>
                        {whatsAppDisclaimerStart} (+351936385141) {whatsAppDisclaimerEnd}
                    </div>
                    <div style={{marginTop: 'var(--spacer-XS)'}}>
                        <a href={privacyPolicyUrl} target="_blank" rel="noopener noreferrer">{t(TK.privacyPolicy)}</a>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default ClientRemindersTab;
